/*
 * allcities-search.js — lazy-loaded city/variant search.
 *
 * The search list (~thousands of <li> + <img>) used to be inlined into every
 * page's HTML 3-4x via header-search/mega-menu, bloating the document to
 * ~3.5MB and freezing the main thread for seconds building the DOM. Now the
 * list HTML is fetched on demand (first focus of a .citySearchInput) from a
 * layout-less product view (templates/product.search-index.liquid) and
 * injected into the nearest `.allcities-mount`. This file holds the search
 * behaviour and is the only thing loaded eagerly (small, cached); it does
 * nothing until the user focuses a search box.
 */
(function () {
  if (window.__allCitiesSearchInit) return;
  window.__allCitiesSearchInit = true;

  // ---- Fuse.js (CDN) loaded lazily, once, on first search use ----
  var _fusePromise = null;
  function loadFuse() {
    if (window.Fuse) return Promise.resolve();
    if (_fusePromise) return _fusePromise;
    _fusePromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/fuse.js@6.6.2';
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return _fusePromise;
  }

  // ---- Search behaviour (ported verbatim from the old inline snippet) ----
  function initAllCitiesSearch(root, payload) {
    if (!root || !payload || !payload.items) return;

  const CARD_THRESHOLD = 30;

  // Inputs stay global so every search box (desktop mega-menu + mobile
  // overlay) drives this instance's list.
  const inputs = document.querySelectorAll('.citySearchInput');

  // Helper: remove flag emojis + lowercase + trim + strip diacritics.
  // Separators (hyphens, en/em dashes, slashes) and any whitespace run are
  // collapsed to a single space so a variant token like "dubai - marina"
  // and a query like "dubai marina" (or "dubai-marina") normalize to the
  // same "dubai marina" — both for Fuse matching and the substring guard.
  const stripFlags = s => s?.replace(/[\u{1F1E6}-\u{1F1FF}]{2}/gu, "") ?? "";
  const stripDiacritics = s => s.normalize("NFD").replace(/\p{Diacritic}/gu, "");
  const normalizeText = s => stripDiacritics(stripFlags(String(s)))
    .toLowerCase()
    .replace(/[\s\u2010-\u2015\/-]+/g, " ")
    .trim();

  /* Aliases / synonyms now live in Shopify metaobject `related_terms` fields,
     delivered via the JSON payload — cities in payload.rel, countries in
     payload.crel. The old hardcoded cityAliases / countryAliases maps were
     removed: they were English-keyed (so they only ever worked in the EN
     store) and are fully superseded by the metafields, which work in every
     locale. normalizeText/stripFlags/stripDiacritics above are still used. */

  // Related-terms lookup (payload.rel): city display-name → extra tokens
  // (english_name + native local_name + the related_terms metafield). Re-key
  // by normalized name and normalize the values so they line up with the tokens
  // we match against. Cities use it by their own name; variants inherit it via
  // their city part (d.ck) so "Tokyo" terms also apply to the Tokyo CUBE/FRAME.
  const relmap = {};
  {
    const relRaw = payload.rel || {};
    for (const k in relRaw) {
      const nk = normalizeText(k);
      if (!nk) continue;
      const vals = (relRaw[k] || []).map(normalizeText).filter(Boolean);
      relmap[nk] = (relmap[nk] || []).concat(vals);
    }
  }

  // Country-terms lookup (payload.crel): country name → exonyms, same shape as
  // relmap. Applied to a city's country tokens for cross-language country
  // search ("Deutschland"/"Allemagne" → German cities in any store).
  const crelmap = {};
  {
    const crelRaw = payload.crel || {};
    for (const k in crelRaw) {
      const nk = normalizeText(k);
      if (!nk) continue;
      const vals = (crelRaw[k] || []).map(normalizeText).filter(Boolean);
      crelmap[nk] = (crelmap[nk] || []).concat(vals);
    }
  }

  // Build search records from the JSON payload (no DOM needed). Each record
  // keeps its source data object (`d`) + its original index (`idx`) so we can
  // render the matched items on demand and preserve Liquid order. Tokens come
  // from the item's own name/title, the legacy alias map, AND the city's
  // related-terms bag (by city part for variants).
  const records = payload.items.map(function (d, idx) {
    const cityCanon = normalizeText(d.dc);         // city: name; variant: full title
    const cityKey = normalizeText(d.ck || d.dc);   // variant: city part; city: name
    const countryCanon = normalizeText(d.dco);

    const cityTokens = new Set([cityCanon]);
    if (cityKey) cityTokens.add(cityKey);
    (relmap[cityKey] || []).forEach(function (t) { cityTokens.add(t); });

    const countryTokens = new Set([countryCanon, ...(crelmap[countryCanon] || [])]);

    return {
      idx: idx,
      d: d,
      city_all: Array.from(cityTokens).filter(Boolean),
      country_all: Array.from(countryTokens).filter(Boolean)
    };
  });

  // Custom Location, flagged with `cl` in the payload: its city tile, the CUBE and
  // the FRAME. Normal searchable records, and together the fallback shown when a
  // query matches nothing — so a search for a city we don't model yet (e.g.
  // "Leipzig") offers to build it instead of dead-ending on an empty list.
  const clRecords = records.filter(function (r) { return r.d.cl; });
  // The City Wishlist's tile (`wl`) is one of them (Alex, 2026-11-02). Shown for a query that found nothing, its link
  // carries the words typed (?q=), so the wishlist opens with that place already looked up; anywhere else, the page.
  const wlRecord = records.find(function (r) { return r.d.wl; });
  let typed = '';
  function wishlistLink(q) {
    const el = wlRecord && cardEls[wlRecord.idx];
    if (el) el.href = wlRecord.d.u + (q ? '?q=' + encodeURIComponent(q) : '');
  }

  const textUl = root.querySelector('.acs-text');
  const cardsBox = root.querySelector('.acs-cards');

  // Pre-build the lightweight all-cities name list ONCE (the empty state and
  // text mode). Names only — no images, no card markup — so it's cheap even
  // for thousands of cities, and filtering is just a class toggle. Card markup
  // (with images) is built on demand for the ≤30 matches of a narrowed query,
  // so we never parse thousands of <img>.
  const textByIdx = {};
  {
    let html = '';
    for (let i = 0; i < records.length; i++) {
      const r = records[i];
      if (r.d.s !== 'cities' || r.d.nl) continue; // `nl`: a tile only (Custom Location)
      html += '<li class="acs-text-item" data-idx="' + r.idx + '">'
            + '<a href="' + r.d.u + '"' + (r.d.f ? ' class="acs-fav"' : '') + '>'
            + esc(r.d.n) + '</a></li>';
    }
    textUl.innerHTML = html;
    textUl.querySelectorAll('.acs-text-item').forEach(function (li) {
      textByIdx[li.getAttribute('data-idx')] = li;
    });
  }

  const fuse = new Fuse(records, {
    keys: [
      { name: "city_all", weight: 0.7 },
      { name: "country_all", weight: 0.3 }
    ],
    threshold: 0.2,
    ignoreLocation: false,
    distance: 100,
    minMatchCharLength: 3,
    includeScore: true
  });

  // For semi-fuzzy results, verify the query actually appears as a substring
  // in the city's token data. This prevents false positives like "china" → "chicago"
  // while preserving near-exact alias hits (score < 0.1) unconditionally.
  function isFalsePositive(result, q) {
    if (q.length < 4 || result.score < 0.1) return false;
    return ![...result.item.city_all, ...result.item.country_all].some(t => t.includes(q));
  }

  // Escape text destined for an innerHTML string (city/variant names, etc.).
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  // Card grouping order + labels. CUBE and trio rows sit under "CUBE";
  // FRAME and GRAND rows share "FRAME & GRAND" (FRAMEs first — rows sort by
  // Liquid order and the payload emits every FRAME before the first GRAND), and
  // each of those cards names its format on a line below the title; the
  // collections — CUBEs in the payload (`s` "cube", `k` "collection") — get a
  // heading of their own. groupOf() only picks the HEADING: every row keeps its
  // own `s`, so "london frame" still scopes to FRAME alone. The city cards carry
  // no heading. Brand labels are literal; the translated ones arrive in the
  // payload (hc cities, hcol collections, hl the descriptive sections).
  const SECTION_ORDER = ['cities', 'cube', 'frame', 'collection', 'blok', 'citywall', 'cover', 'plaque', 'note', 'config'];
  function groupOf(d) {
    if (d.s === 'cube' && d.k === 'collection') return 'collection';
    if (d.s === 'grand') return 'frame';
    return d.s;
  }
  const hl = payload.hl || {};
  const SECTION_LABELS = {
    cities: payload.hc || 'Cities',
    cube: 'CUBE',
    frame: 'FRAME & GRAND',
    collection: payload.hcol || 'Collections',
    blok: 'BLOK',
    citywall: 'CITYWALL',
    cover: hl.cover || 'Display Cover',
    plaque: hl.plaque || 'Engraved Plaque',
    note: hl.note || 'Card with Note',
    config: hl.config || 'Configurators'
  };

  // Clearance rows (`sl` in the payload) get the localized Clearance Sale chip,
  // reusing the product cards' own .badge/.badge--cube classes so it matches
  // them exactly. The label ships in the payload so it stays translated.
  const SALE_LABEL = payload.hsl || '';
  // Light-weight lead-in on the city cards ("Discover London"), translated.
  const DISCOVER = payload.hd || '';
  function saleBadge(d) {
    if (!d.sl || !SALE_LABEL) return '';
    return '<span class="badge badge--cube acs-badge">' + esc(SALE_LABEL) + '</span>';
  }

  // Thumbnails are asked for as progressive JPEG. Left to itself Shopify's CDN
  // answers with AVIF, and most of these thumbnails are not in its cache: a
  // picture nobody has asked for lately is encoded on request, which the
  // `server-timing` header on each image puts at ~500ms for AVIF against ~100ms
  // for JPEG — about half a second off every uncached thumbnail, for ~3KB more
  // per picture. One format for every browser is also one cached copy instead of
  // one per format. The PNGs — GRAND's "available on request" picture — keep
  // theirs, since a JPEG has no transparency, and so do the animated pictures
  // (`an`): a JPEG would stop them, and they are already sent at the one width
  // the CDN hands over without re-encoding every frame.
  function thumbSrc(u, animated) {
    if (animated || /\.png(\?|$)/i.test(u)) return u;
    return u + (u.indexOf('?') < 0 ? '?' : '&') + 'format=pjpg';
  }

  // PRODUCT RESULTS ARE TILES (2026-10-12): small versions of the product cards - the picture whole and centred on the
  // grey tile, multiplied into it (snippet CSS, .acs-tile), with the name, the line under it and the price below. The
  // pictures are the cards' own renders (the payload sends 400px; 200, 400 and 600 are offered), uncropped, so a FRAME
  // shows its lid and a BLOK its whole table; an upright GRAND is turned inside its tile. FRAME and GRAND share their
  // heading, so their line names the format after the area. Cities keep their photo strips, and a result without a
  // picture (the configurators) keeps its text row - cardHtml below.
  function tileHtml(d) {
    const isSize = d.s === 'frame' && /^FRAME/.test(d.sub || '');
    let sub = d.e || d.sub || '';
    if (d.s === 'frame' || d.s === 'grand') {
      const format = isSize ? d.sub : (d.s === 'grand' ? 'GRAND' : 'FRAME');
      sub = isSize ? format : [d.e || d.sub, format].filter(Boolean).join(' · ');
    }
    const srcset = (!d.an && /[?&]width=\d+/.test(d.i))
      ? ' srcset="' + [200, 400, 600].map(function (w) { return thumbSrc(d.i.replace(/([?&])width=\d+/, '$1width=' + w)) + ' ' + w + 'w'; }).join(', ') + '" sizes="(max-width: 699px) 33vw, 220px"'
      : '';
    const sale = (d.sl && SALE_LABEL) ? '<span class="badge product-card__label clearance_sale acs-tile__label">' + esc(SALE_LABEL) + '</span>' : '';
    const price = d.p
      ? '<span class="acs-tile__price"><b>' + esc(d.p) + '</b>' + (d.w ? '<s>' + esc(d.w) + '</s>' : '') + '</span>'
      : '';
    // A clearance FRAME's size, for the true-to-size scale beside a GRAND (the snippet's CSS): its line is the size,
    // "FRAME Wide" (79 cm) or "FRAME" - the 49 cm one, the only FRAME sold off.
    const sizeClass = isSize ? (/wide/i.test(d.sub) ? ' acs-tile--frame-wide' : ' acs-tile--frame-49') : '';
    return '<a href="' + d.u + '" class="acs-tile acs-tile--' + d.s + sizeClass + '">'
         + '<span class="acs-tile__pic">' + sale
         + '<img src="' + thumbSrc(d.i, d.an) + '"' + srcset + ' width="400" height="400" loading="lazy" decoding="async" alt=""' + (d.up ? ' class="is-upright"' : '') + '>'
         + '</span>'
         + '<span class="acs-tile__name">' + esc(d.n) + '</span>'
         + (sub ? '<span class="acs-tile__sub">' + esc(sub) + '</span>' : '')
         + price
         + '</a>';
  }

  function cardHtml(d) {
    // Tiles need the list that carries their prices and their CSS (the snippet's): a list cached from before them
    // (no `p` on its rows) keeps the rows it was made for, so the two never meet half-way.
    if (d.s !== 'cities' && d.i && d.p !== undefined) return tileHtml(d);
    // TITLE LINE, then ONE LINE BELOW IT. The line beside the title is the area
    // ("London Westminster") or, on a city card, nothing; the line below is the
    // city's local name ("(Antwerpen)"). The two swapped places on 2026-09-15 and
    // kept their own looks — .acs-sub stays light and smaller, .acs-local stays
    // light — while the title line keeps its ellipsis, so a long area is cut with
    // "…" rather than wrapping.
    //
    // FRAME and GRAND cards name their format on a line below the title, set like a
    // city's local name (heading size, light) and subdued — FRAME and GRAND share one
    // heading, so this is what tells them apart. A clearance FRAME's sub is already
    // its size ("FRAME" / "FRAME Wide") and is used for that line rather than
    // beside the title.
    let info;
    if (d.s === 'cities') {
      // Custom Location's tile carries its card's line above the name (`e`) instead of "Discover".
      let discover = (DISCOVER && !d.e) ? '<span class="acs-discover">' + esc(DISCOVER) + '</span> ' : '';
      let after = '';
      // A format's card says its own sentence (`dp`, "Discover the FRAME", "Entdecken Sie das GRAND"): the words
      // around the name are set light, as "Discover" is on a city.
      const at = d.dp ? d.dp.indexOf(d.n) : -1;
      if (at > -1) {
        const before = d.dp.slice(0, at).trim();
        const rest = d.dp.slice(at + d.n.length).trim();
        discover = before ? '<span class="acs-discover">' + esc(before) + '</span> ' : '';
        after = rest ? ' <span class="acs-discover">' + esc(rest) + '</span>' : '';
      }
      const cityEyebrow = d.e ? '<span class="smallcaps acs-eyebrow">' + esc(d.e) + '</span>' : '';
      // Wrapped in the title's own heading span, as it was when it sat beside the
      // title, so it inherits the same size and case and only its position changes.
      const below = d.loc ? '<span class="h6 acs-name"><span class="acs-local">(' + esc(d.loc) + ')</span></span>' : '';
      info = cityEyebrow
           + '<span class="h6 acs-name' + (d.f ? ' acs-name--featured' : '') + '">' + discover + esc(d.n) + after + '</span>'
           + below
           + saleBadge(d);
    } else {
      // Display Cover and Engraved Plaque cards name the product ("Display Cover
      // miniCUBE"), bold, and put the variant ("Acrylic") on the line below, light,
      // set like a city's local name. Beside the title it was the part the ellipsis
      // cut, and it is the part that tells these cards apart.
      const variantBelow = d.s === 'cover' || d.s === 'plaque';
      const isSize = d.s === 'frame' && /^FRAME/.test(d.sub || '');
      const beside = (d.sub && !isSize && !variantBelow) ? ' <span class="acs-sub acs-sub--beside">' + esc(d.sub) + '</span>' : '';
      let size = '';
      if (d.s === 'frame' || d.s === 'grand') size = isSize ? d.sub : (d.s === 'grand' ? 'GRAND' : 'FRAME');
      let below = size ? '<span class="h6 acs-name"><span class="acs-local acs-size text-subdued">' + esc(size) + '</span></span>' : '';
      if (variantBelow && d.sub) below = '<span class="h6 acs-name"><span class="acs-local">' + esc(d.sub) + '</span></span>';
      // The eyebrow (`e`), on the rows that have no city to name, carries the line
      // their card shows under the name on /collections/cube: "Your choice of cities".
      const eyebrow = d.e ? '<span class="smallcaps acs-eyebrow">' + esc(d.e) + '</span>' : '';
      info = eyebrow
           + '<span class="h6 acs-name acs-name--variant">' + esc(d.n) + beside + '</span>'
           + below
           + saleBadge(d);
    }
    // An upright GRAND (`up`) gets its thumbnail turned 90°, as on the collection page, and
    // its wrapper nudged right (the nudge sits on the unrotated parent so it stays horizontal).
    // Thumbnail only when the item has an image; items without one (e.g. the
    // configurators) render text-only with no empty 60px gap.
    // `p45` rows are a CUBE's 4:5 combo, shown whole (.acs-thumb--45).
    // A city's photo fills its tile, two product tiles wide (about 440px), so it comes at three
    // widths (the payload sends 800).
    const citySizes = (d.s === 'cities' && d.i && /[?&]width=\d+/.test(d.i))
      ? ' srcset="' + [400, 800, 1200].map(function (w) { return thumbSrc(d.i.replace(/([?&])width=\d+/, '$1width=' + w)) + ' ' + w + 'w'; }).join(', ') + '" sizes="(max-width: 699px) 100vw, 460px"'
      : '';
    const thumb = d.i
      ? '<span class="acs-thumb-wrap acs-thumb-wrap--' + d.s + (d.up ? ' acs-thumb-wrap--upright' : '') + '"><img src="' + thumbSrc(d.i, d.an) + '"' + citySizes + ' width="60" height="60" loading="lazy" decoding="async" class="acs-thumb' + (d.up ? ' acs-thumb--upright' : '') + (d.p45 ? ' acs-thumb--45' : '') + '" alt=""></span>'
      : '';
    // Names that wrap rather than end in "…": see .acs-card--wrap in the snippet.
    const wrap = (d.wr || d.s === 'cover' || d.s === 'plaque') ? ' acs-card--wrap' : '';
    return '<a href="' + d.u + '" class="acs-card acs-card--' + d.s + wrap + '">'
         + thumb
         + '<span class="acs-card-info">' + info + '</span></a>';
  }

  // Build card tiles for the matched records, grouped by section. Items keep
  // their original Liquid order within a section (sort by idx), not Fuse score
  // order — matches the previous behaviour. Empty groups are simply skipped,
  // so a query that only hits CUBE never shows a dangling FRAME/GRAND/BLOK heading.
  //
  // Each card is built once, the first time its record matches, and the same
  // element is reused after that. Rebuilding the grid from an HTML string on
  // every keystroke replaced every <img>: from "lon" to "london" the London
  // thumbnails were made four times, each new one blank until it decoded again.
  // Moving the existing element keeps a thumbnail that has loaded, or is still
  // loading, on screen.
  const cardEls = {};
  const cardTpl = document.createElement('template');
  function cardEl(rec) {
    if (!cardEls[rec.idx]) {
      cardTpl.innerHTML = cardHtml(rec.d);
      cardEls[rec.idx] = cardTpl.content.firstElementChild;
    }
    return cardEls[rec.idx];
  }

  function renderCards(results) {
    const bySection = {};
    results.forEach(function (r) {
      const group = groupOf(r.item.d);
      (bySection[group] = bySection[group] || []).push(r.item);
    });
    const frag = document.createDocumentFragment();
    SECTION_ORDER.forEach(function (sec) {
      const arr = bySection[sec];
      if (!arr || !arr.length) return;
      arr.sort(function (a, b) { return a.idx - b.idx; });
      // The cities get no heading. Every other group does, with a divider only
      // when something sits above it — a query that finds no city starts the
      // grid on a heading, and a rule over nothing would float at the top.
      if (sec !== 'cities') {
        const heading = document.createElement('div');
        heading.className = 'acs-heading h5' + (frag.firstChild ? ' acs-heading--divider' : '');
        heading.textContent = SECTION_LABELS[sec];
        frag.appendChild(heading);
      }
      // The cities are photo tiles in a row of their own (.acs-city-tiles in the snippet).
      if (sec === 'cities') {
        const tiles = document.createElement('div');
        tiles.className = 'acs-city-tiles';
        arr.forEach(function (rec) { tiles.appendChild(cardEl(rec)); });
        frag.appendChild(tiles);
        return;
      }
      arr.forEach(function (rec) { frag.appendChild(cardEl(rec)); });
    });
    cardsBox.replaceChildren(frag);
  }

  function apply(q, results, sectionFilter, kindFilter) {
    // No query → empty state: show every city name, no cards.
    if (!q) {
      root.classList.remove('acs--cards');
      cardsBox.innerHTML = '';
      for (const k in textByIdx) textByIdx[k].classList.remove('acs-text-item--hidden');
      return;
    }

    let filtered = results.filter(function (r) { return !isFalsePositive(r, q); });
    // Section-scoped query (e.g. "New York CUBE") → keep only that product
    // section, then always show it as cards (it's a deliberate product pick).
    if (sectionFilter) filtered = filtered.filter(function (r) { return r.item.d.s === sectionFilter; });
    // Kind-scoped the same way, for the groups that live inside a section — the
    // trios and the collections are both CUBEs, so a section cannot separate them.
    if (kindFilter) filtered = filtered.filter(function (r) { return r.item.d.k === kindFilter; });

    // Nothing matched — a city we don't model yet, a typo, or a product-scoped
    // query with no hits. Offer Custom Location instead of showing an empty list,
    // so the search is never a dead end: its tile, the CUBE and the FRAME, each in
    // its own section.
    if (!filtered.length && clRecords.length) {
      root.classList.add('acs--cards');
      renderCards(clRecords.map(function (r) { return { item: r }; }));
      wishlistLink(typed);
      return;
    }

    // A scoped query is a deliberate product pick, so it always shows as cards.
    // Without that, "trio" matched all thirty-three of them, went over
    // CARD_THRESHOLD, and fell back to text mode — which lists city names only,
    // so the one query that should have returned every trio returned nothing.
    const cardMode = (sectionFilter || kindFilter)
      ? filtered.length > 0
      : (filtered.length > 0 && filtered.length <= CARD_THRESHOLD);

    if (cardMode) {
      root.classList.add('acs--cards');
      renderCards(filtered);
      wishlistLink('');
      return;
    }

    // Broad match (or none) → text mode: matched city names only (variants
    // stay hidden), via class toggle on the pre-built list.
    root.classList.remove('acs--cards');
    cardsBox.innerHTML = '';
    const keep = {};
    filtered.forEach(function (r) { if (r.item.d.s === 'cities') keep[r.item.idx] = 1; });
    for (const k in textByIdx) textByIdx[k].classList.toggle('acs-text-item--hidden', !keep[k]);
  }

  // Product-type words that scope a query to a section. Brand names, so the
  // same in every language. "<city> <type>" (any order) filters to that
  // section instead of trying to match the combined phrase as one string.
  const TYPE_KEYWORDS = { cube: 'cube', cubes: 'cube', frame: 'frame', frames: 'frame', grand: 'grand', grands: 'grand', blok: 'blok', bloks: 'blok', citywall: 'citywall' };

  /*
   * A FORMAT ON ITS OWN (Alex, 2026-10-22). "frame" alone matched no city and fell through to Custom Location, which
   * read as "nothing found". It now shows the format's card - "Discover the FRAME", to its overview page, drawn like a
   * city's photo card - then its most popular cities: the format's own rows (no Sets, Collections, clearance or
   * cities on request), by the variants' popular_rank (`pr`), one per city, twelve. The payload carries each format's page, name
   * and picture (`fmt`) and the heading (`hp`, "Popular [format]s").
   */
  const FORMATS = payload.fmt || {};
  const POPULAR_LABEL = payload.hp || '';
  const formatEls = {};
  function renderFormat(sec) {
    const f = FORMATS[sec];
    if (!formatEls[sec]) {
      cardTpl.innerHTML = cardHtml({ s: 'cities', n: f.n, u: f.u, i: f.i, dp: f.d });
      formatEls[sec] = cardTpl.content.firstElementChild;
    }
    const seen = {};
    const top = records
      .filter(function (r) { return r.d.s === sec && !r.d.k && !r.d.sl && !r.d.cl && !r.d.or && r.d.i && r.d.p !== undefined; })
      .sort(function (a, b) { return (a.d.pr || 9999) - (b.d.pr || 9999) || a.idx - b.idx; })
      .filter(function (r) {
        // one tile per city, its most popular area: New York's Midtown, Downtown and Brooklyn took three of twelve
        const city = r.d.ck || r.d.n;
        if (seen[city]) return false;
        seen[city] = 1;
        return true;
      })
      .slice(0, 12);
    const frag = document.createDocumentFragment();
    const tiles = document.createElement('div');
    tiles.className = 'acs-city-tiles';
    tiles.appendChild(formatEls[sec]);
    frag.appendChild(tiles);
    if (top.length && POPULAR_LABEL) {
      const heading = document.createElement('div');
      heading.className = 'acs-heading h5 acs-heading--divider';
      heading.textContent = POPULAR_LABEL.replace('[format]', f.n);
      frag.appendChild(heading);
      top.forEach(function (r) { frag.appendChild(cardEl(r)); });
    }
    root.classList.add('acs--cards');
    cardsBox.replaceChildren(frag);
  }

  /*
   * Words that name a GROUP INSIDE a section rather than a section. A trio and a
   * collection are both CUBEs, so TYPE_KEYWORDS cannot tell them apart; each row
   * carries `k` instead, and these words scope to it.
   *
   * Unlike a bare type word, a bare kind word IS a query. "cube" on its own means
   * two hundred cities and is better answered by the empty-state list, but "trio"
   * on its own means the thirty-three trios — a list worth showing, and the one a
   * shopper who knows the word is asking for. Plurals included because that is how
   * people ask for a range.
   *
   * Brand names, so the same in every language — the same reasoning as TYPE_KEYWORDS.
   */
  const KIND_KEYWORDS = {
    // "set" since the trios became CUBE Sets (2026-10-15); "trio" still finds them.
    set: 'trio', sets: 'trio', trio: 'trio', trios: 'trio',
    collection: 'collection', collections: 'collection'
  };

  inputs.forEach(input => {
    input.addEventListener("input", function () {
      const raw = normalizeText(this.value);
      typed = this.value.trim();

      // Pull out a product-type word and a kind word, if present, and search the
      // remainder as the city. Only scope by TYPE when a usable (≥3 char) city
      // remainder is left — a bare "cube"/"frame" falls through to the normal
      // path (empty state).
      let sectionFilter = null;
      let kindFilter = null;
      const rest = [];
      raw.split(' ').forEach(function (w) {
        if (!sectionFilter && TYPE_KEYWORDS[w]) sectionFilter = TYPE_KEYWORDS[w];
        else if (!kindFilter && KIND_KEYWORDS[w]) kindFilter = KIND_KEYWORDS[w];
        else rest.push(w);
      });
      const cleaned = rest.join(' ').trim();

      // A format's name on its own: its card and its popular cities (see renderFormat).
      if (sectionFilter && !kindFilter && cleaned.length < 3 && FORMATS[sectionFilter]) {
        renderFormat(sectionFilter);
        return;
      }

      let q, sf, kf;
      if (kindFilter) {
        // "storyteller collection" narrows within the kind; "collection" alone
        // asks for all of them, and there is nothing to fuzzy-match — the kind
        // filter in apply() is the whole query, so every row of that kind comes
        // back. `q` still has to be truthy or apply() reads it as the empty state.
        q = cleaned.length >= 3 ? cleaned : raw;
        sf = sectionFilter;
        kf = kindFilter;
      } else if (sectionFilter && cleaned.length >= 3) {
        q = cleaned; sf = sectionFilter; kf = null;
      } else {
        // Treat 1–2 char queries as no query yet — keeps the full empty-state
        // list on screen until the user commits to a 3-character query.
        q = raw.length >= 3 ? raw : "";
        sf = null; kf = null;
      }

      // A bare kind word matches nothing by name, so the kind filter stands in
      // for the search: every row of that kind, in Liquid order.
      const bareKind = kf && cleaned.length < 3;
      const results = bareKind
        ? records.map(function (r) { return { item: r, score: 0 }; })
        : (q ? fuse.search(q) : []);

      apply(q, results, sf, kf);
    });
  });

  }

  // ---- Lazy loader: fetch the list HTML on first search focus ----
  var _htmlPromise = null;
  function fetchListHTML(src) {
    if (_htmlPromise) return _htmlPromise;
    _htmlPromise = fetch(src, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('allcities-search HTTP ' + r.status);
      return r.text();
    });
    return _htmlPromise;
  }

  // Nearest `.allcities-mount` to the focused input (tightest ancestor first).
  function findMountFor(input) {
    var el = input;
    while (el && el !== document.body) {
      var m = el.querySelector('.allcities-mount');
      if (m) return m;
      el = el.parentElement;
    }
    return document.querySelector('.allcities-mount');
  }

  function loadMount(mount) {
    if (!mount || mount.dataset.acsLoaded) return;
    var src = mount.getAttribute('data-allcities-src');
    if (!src) return;
    mount.dataset.acsLoaded = '1';
    Promise.all([fetchListHTML(src), loadFuse()]).then(function (res) {
      // The fetched view is now tiny: a <style> block, an empty results
      // container, and a <script type="application/json"> data payload. The
      // big DOM (name list + card tiles) is built by initAllCitiesSearch, so
      // this innerHTML assignment no longer parses thousands of nodes/images.
      mount.innerHTML = res[0];
      var root = mount.querySelector('.acs-root');
      var dataEl = mount.querySelector('.acs-data');
      if (root && dataEl) {
        var payload = null;
        try { payload = JSON.parse(dataEl.textContent); }
        catch (e) { if (window.console) console.warn('allcities-search: bad JSON', e); }
        if (payload) initAllCitiesSearch(root, payload);
      }
    }).catch(function (err) {
      mount.dataset.acsLoaded = '';   // allow a retry on the next focus
      if (window.console) console.warn('allcities-search: load failed', err);
    });
  }

  // Load on focus (covers desktop click-into-input and keyboard users)…
  document.addEventListener('focusin', function (e) {
    var input = e.target.closest && e.target.closest('.citySearchInput');
    if (!input) return;
    loadMount(findMountFor(input));
  });

  // …and load as soon as a search box becomes VISIBLE — i.e. the moment the
  // search panel opens — so the list is there to browse without having to
  // tap the field first. On mobile, opening the panel doesn't auto-focus the
  // input, so focus alone left the list empty until the user tapped it. The
  // input sits at the top of the panel, so it's visible immediately on open
  // (no scrolling needed). Inputs are display:none while the panel is closed,
  // so this never fires at page load.
  function observeSearchInputs() {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          loadMount(findMountFor(entry.target));
          io.unobserve(entry.target);
        }
      });
    });
    document.querySelectorAll('.citySearchInput').forEach(function (inp) { io.observe(inp); });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeSearchInputs);
  } else {
    observeSearchInputs();
  }

  // Fully prepare search in the background once the page is idle: fetch the
  // list HTML, load Fuse, AND inject the DOM + build the Fuse index for every
  // mount. Previously we only warmed the network (fetch + Fuse) and left the
  // expensive work — innerHTML parse of ~thousands of nodes + index build —
  // to run synchronously on first focus, which blocked the main thread for
  // 1–2s the moment the user opened search. Doing it here removes that stall:
  // by open time the list and index already exist, so it injects instantly.
  //
  // Safe to build early because the search panels are display:none while
  // closed — injecting a detached/hidden subtree costs no layout or paint,
  // and the `loading="lazy"` thumbnails don't fetch until the panel is shown.
  // Running via requestIdleCallback AFTER `load` means it never competes with
  // initial page render. Each mount builds in its own idle slot so a single
  // long task can't jank. loadMount() flips `acsLoaded` synchronously, so if
  // the user opens search before this finishes, the focus/intersection paths
  // become no-ops and the in-flight build still completes — no double work.
  function prefetchSearch() {
    var mounts = document.querySelectorAll('.allcities-mount');
    if (!mounts.length) return;
    loadFuse();
    mounts.forEach(function (mount, i) {
      var build = function () { loadMount(mount); };
      if ('requestIdleCallback' in window) {
        requestIdleCallback(build, { timeout: 4000 });
      } else {
        setTimeout(build, 1400 + i * 400);
      }
    });
  }
  function schedulePrefetch() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(prefetchSearch, { timeout: 3000 });
    } else {
      setTimeout(prefetchSearch, 1200);
    }
  }
  if (document.readyState === 'complete') {
    schedulePrefetch();
  } else {
    window.addEventListener('load', schedulePrefetch, { once: true });
  }
})();
